import { useMutation } from "@apollo/client";
import { compact } from "lodash";
import { useMemo, useState } from "react";
import { useCallback } from "react";
import {
  ComplianceProgramListItemFragment,
  ComplianceProgramState,
  DeleteProgramMutation,
  DeleteProgramMutationVariables,
  ExportComplianceProgramToCsvMutationMutation,
  ExportComplianceProgramToCsvMutationMutationVariables,
  GetComplianceProgramQuery,
  OrganizationRole,
  ProgramParticipantUpdatesFragment,
  SaveComplianceProgramMutation,
  SaveComplianceProgramMutationVariables,
} from "types/graphql-schema";

import {
  currentOrganizationVar,
  currentUserVar,
  successNotificationVar,
} from "@cache/cache";
import Dropdown from "@components/dropdown/dropdown";
import { removeConsecutiveDividers } from "@components/dropdown/dropdown-helpers";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import { useLink } from "@components/link/link-helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import createOrUpdateComplianceProgramMutation from "../graphql/create-or-update-compliance-program-mutation";
import deleteComplianceProgramMutation from "../graphql/delete-compliance-program-mutation";
import exportComplianceProgramToCsvMutation from "../graphql/export-compliance-program-to-csv-mutation";
import getComplianceProgramQuery from "../graphql/get-compliance-program-admin-query";
import getComplianceProgramsQuery from "../graphql/get-compliance-programs-query";
import { NO_PARTICIPANTS_ERROR } from "../helpers";
import { useProgramsSection } from "../programs-section-context";
import ComplianceProgramDetailsDialog from "./compliance-program-details-dialog";
import { isKickoffDatePast } from "./compliance-program-form/form-data";
import ComplianceProgramUserChangesDialog from "./compliance-program-user-changes-dialog";

const ComplianceProgramActionDropdown = ({
  complianceProgram,
  availableAppliesToUpdates,
}: {
  complianceProgram:
    | NonNullable<GetComplianceProgramQuery["complianceProgram"]>
    | ComplianceProgramListItemFragment;
  availableAppliesToUpdates?: ProgramParticipantUpdatesFragment | null;
}) => {
  const link = useLink();
  const { basePath, listUrl, noun } = useProgramsSection();
  const nounLower = noun.toLowerCase();
  const organization = currentOrganizationVar();
  const currentUser = currentUserVar();
  const isGlobalAdmin =
    organization.userMembership?.role === OrganizationRole.GlobalAdmin;

  const complianceProgramId = complianceProgram.id;
  const complianceProgramState = complianceProgram.state;
  const complianceProgramTitle = complianceProgram.title;
  const canUpdate = complianceProgram.canUpdate;
  const canDelete = complianceProgram.canDelete;

  const [
    isShowingProgramUserChangesDialog,
    setIsShowingProgramUserChangesDialog,
  ] = useState(false);
  const [isShowingProgramDetailsDialog, setIsShowingProgramDetailsDialog] =
    useState(false);

  const { ConfirmationDialog: ConfirmDeleteDialog, confirm: confirmDelete } =
    useConfirm(
      "Are you sure?",
      `Are you sure you want to delete this ${nounLower}? All assessment data will be deleted.`,
    );

  const { ConfirmationDialog: ConfirmCloseDialog, confirm: confirmClose } =
    useConfirm(
      `Close this ${nounLower}?`,
      `This ends the cycle. Anyone part way through can no longer change or submit their answers. The ${nounLower} then only shows up under the "Closed" filter. You can reopen it at any time.`,
    );

  const { ConfirmationDialog: ConfirmKickoffDialog, confirm: confirmKickoff } =
    useConfirm(
      "This will kick off now",
      `The start date has already passed, so publishing sends the kickoff to everyone in this ${nounLower} straight away.`,
    );

  const [saveComplianceProgram, { loading: isPublishingProgram }] = useMutation<
    SaveComplianceProgramMutation,
    SaveComplianceProgramMutationVariables
  >(createOrUpdateComplianceProgramMutation);

  const [deleteProgram, { loading: isDeletingProgram }] = useMutation<
    DeleteProgramMutation,
    DeleteProgramMutationVariables
  >(deleteComplianceProgramMutation);

  const [exportComplianceProgramToCsv, { loading: isExportingCsv }] =
    useMutation<
      ExportComplianceProgramToCsvMutationMutation,
      ExportComplianceProgramToCsvMutationMutationVariables
    >(exportComplianceProgramToCsvMutation);

  const errorMatches = useMemo(
    () => [
      {
        match: "no actions to take",
        title: `Add at least one step to the ${nounLower} before publishing`,
      },
      {
        // There is no form here to show it in, so it is a toast.
        match: NO_PARTICIPANTS_ERROR,
        title: `This ${nounLower} has no participants`,
        description:
          "Nobody matches its participant rule, so it would be sent to nobody. Change who takes part before you publish.",
      },
      {
        // The server lists what is missing. Publishing from here skips the
        // form, so this is where a draft saved half-finished gets stopped.
        match: "can't be published yet",
        title: `${noun} not published`,
        showMessage: true,
      },
    ],
    [noun, nounLower],
  );

  const publishProgram = useCallback(async () => {
    if (
      complianceProgramState === ComplianceProgramState.Draft &&
      isKickoffDatePast({
        ongoing: complianceProgram.ongoing,
        startDate: complianceProgram.startDate ?? undefined,
      }) &&
      !(await confirmKickoff())
    ) {
      return;
    }
    // The same mutation publishes a draft, resumes a paused review and reopens a closed
    // one. Only the wording differs.
    const doneTitle =
      complianceProgramState === ComplianceProgramState.Closed
        ? `${noun} reopened`
        : complianceProgramState === ComplianceProgramState.Paused
          ? `${noun} resumed`
          : `${noun} published`;
    saveComplianceProgram({
      variables: {
        complianceProgramId,
        state: ComplianceProgramState.Published,
        organizationId: organization.id,
      },
      context: { errorMatches },
      refetchQueries: [getComplianceProgramQuery, getComplianceProgramsQuery],
      onCompleted: () =>
        successNotificationVar({
          title: doneTitle,
        }),
      // The GraphQL link already shows the toast. Without this the refusal is
      // also an unhandled rejection, which Sentry reports as a crash.
      onError: () => {},
    });
  }, [
    complianceProgramId,
    complianceProgramState,
    complianceProgram.ongoing,
    complianceProgram.startDate,
    confirmKickoff,
    organization,
    errorMatches,
    saveComplianceProgram,
    noun,
  ]);

  const handleDeleteProgram = useCallback(async () => {
    const confirm = await confirmDelete();
    if (!confirm) return;
    deleteProgram({
      variables: {
        complianceProgramId,
      },
      context: { errorMatches },
      refetchQueries: [getComplianceProgramsQuery],
      onCompleted: () => {
        successNotificationVar({ title: `${noun} deleted` });
        link.redirect(listUrl);
      },
    });
  }, [
    confirmDelete,
    deleteProgram,
    complianceProgramId,
    errorMatches,
    link,
    listUrl,
    noun,
  ]);

  const handlePauseProgram = useCallback(async () => {
    saveComplianceProgram({
      variables: {
        complianceProgramId,
        state: ComplianceProgramState.Paused,
      },
      context: { errorMatches },
      refetchQueries: [getComplianceProgramQuery, getComplianceProgramsQuery],
      onCompleted: () =>
        successNotificationVar({
          title: `${noun} paused`,
        }),
    });
  }, [saveComplianceProgram, complianceProgramId, errorMatches, noun]);

  const handleCloseProgram = useCallback(async () => {
    const confirm = await confirmClose();
    if (!confirm) return;
    saveComplianceProgram({
      variables: {
        complianceProgramId,
        state: ComplianceProgramState.Closed,
      },
      context: { errorMatches },
      refetchQueries: [getComplianceProgramQuery, getComplianceProgramsQuery],
      onCompleted: () =>
        successNotificationVar({
          title: `${noun} closed`,
        }),
    });
  }, [
    confirmClose,
    saveComplianceProgram,
    complianceProgramId,
    errorMatches,
    noun,
  ]);

  const handleExportCsv = useCallback(() => {
    exportComplianceProgramToCsv({
      variables: {
        complianceProgramId,
      },
      onCompleted: () => {
        successNotificationVar({
          title: `CSV export will be emailed to ${currentUser.email}. The link will expire in two hours.`,
        });
      },
    });
  }, [complianceProgramId, currentUser.email, exportComplianceProgramToCsv]);

  const dropdownOptions: DropdownOptionType[] = useMemo(() => {
    return removeConsecutiveDividers(
      compact([
        {
          label: `Duplicate ${nounLower}`,
          onClick: () =>
            link.redirect(`${basePath}/${complianceProgramId}/duplicate`),
          disabled: isPublishingProgram || isDeletingProgram || isExportingCsv,
        },
        canUpdate?.permission && {
          label: `Edit ${nounLower}`,
          onClick: () =>
            link.redirect(`${basePath}/${complianceProgramId}/edit`),
          disabled: isPublishingProgram || isDeletingProgram || isExportingCsv,
        },
        canUpdate?.permission &&
          complianceProgramState === ComplianceProgramState.Published && {
            label: `Pause ${nounLower}`,
            onClick: handlePauseProgram,
            disabled:
              isPublishingProgram || isDeletingProgram || isExportingCsv,
          },
        canUpdate?.permission &&
          complianceProgramState !== ComplianceProgramState.Published && {
            label:
              complianceProgramState === ComplianceProgramState.Draft
                ? "Publish"
                : complianceProgramState === ComplianceProgramState.Closed
                  ? "Reopen"
                  : "Resume",
            onClick: publishProgram,
            disabled: isPublishingProgram || isExportingCsv,
          },
        canUpdate?.permission &&
          (complianceProgramState === ComplianceProgramState.Published ||
            complianceProgramState === ComplianceProgramState.Paused) && {
            label: `Close ${nounLower}`,
            onClick: handleCloseProgram,
            disabled:
              isPublishingProgram || isDeletingProgram || isExportingCsv,
          },
        isGlobalAdmin && {
          label: `Export ${nounLower} data`,
          onClick: handleExportCsv,
          disabled: isPublishingProgram || isDeletingProgram || isExportingCsv,
        },
        { type: "divider" },
        availableAppliesToUpdates?.hasUpdates && {
          label: `Update ${noun} Participants`,
          onClick: () => setIsShowingProgramUserChangesDialog(true),
        },
        {
          label: `View ${noun} Details`,
          onClick: () => setIsShowingProgramDetailsDialog(true),
        },
        { type: "divider" },
        canDelete?.permission && {
          label: `Delete ${nounLower}`,
          onClick: handleDeleteProgram,
          textColor: "text-red-700 hover:text-red-900",
          disabled: isPublishingProgram || isDeletingProgram || isExportingCsv,
        },
      ]),
    );
  }, [
    basePath,
    canDelete,
    canUpdate,
    complianceProgramId,
    complianceProgramState,
    availableAppliesToUpdates,
    handleCloseProgram,
    handleDeleteProgram,
    handleExportCsv,
    handlePauseProgram,
    isGlobalAdmin,
    isDeletingProgram,
    isExportingCsv,
    isPublishingProgram,
    link,
    publishProgram,
    noun,
    nounLower,
  ]);

  if (dropdownOptions.length === 0) return null;

  return (
    <>
      <Dropdown
        options={dropdownOptions}
        align="end"
        gap={8}
        aria-label={`${noun} actions`}
      />
      <ConfirmDeleteDialog />
      <ConfirmCloseDialog />
      <ConfirmKickoffDialog />
      {isShowingProgramUserChangesDialog && availableAppliesToUpdates && (
        <ComplianceProgramUserChangesDialog
          onClose={() => setIsShowingProgramUserChangesDialog(false)}
          complianceProgramId={complianceProgramId}
          complianceProgramTitle={complianceProgramTitle}
          availableAppliesToUpdates={availableAppliesToUpdates}
        />
      )}
      {isShowingProgramDetailsDialog && (
        <ComplianceProgramDetailsDialog
          complianceProgram={complianceProgram}
          onClose={() => setIsShowingProgramDetailsDialog(false)}
        />
      )}
    </>
  );
};
export default ComplianceProgramActionDropdown;
